'use client';

/**
 * PrintView — Phase A8 scaffold for regulator / board / family /
 * audit PDF + print exports.
 *
 * Strips dashboard chrome (sidebar, topbar) at print time and adds:
 *   - Branded header with logo + tenant name + report title
 *   - Branded footer with page number + generated-at timestamp
 *   - Page-break rules for tables, sections, and long lists
 *
 * Usage pattern:
 *
 *   <PrintView
 *     title="Incident report — INC-12345"
 *     tenantName="Cypress Park Aged Care"
 *     generatedAt="27 May 2026 14:32"
 *   >
 *     <YourPrintableContent />
 *   </PrintView>
 *
 * The wrapper renders ON-screen too (mostly for preview), so the
 * consumer's app can render the same component at `/incidents/[id]/print`
 * and the user prints from there. Browser print dialog handles the
 * actual PDF generation.
 *
 * Spec: `UX_PRODUCTION_ROADMAP.md` §A8 (Print / PDF view scaffold).
 *
 * Used by (per the spec): incident PDF, board pack, audit prep,
 * statement, invoice, complaint summary.
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { PageTitle } from '../typography';

export interface PrintViewProps {
  /** Report title — rendered in the header. */
  title: string;
  /** Optional subtitle (e.g. "Generated for Quality Standards review"). */
  subtitle?: React.ReactNode;
  /** Tenant name shown in the header. */
  tenantName?: string;
  /** Human-readable timestamp (tz-aware, formatted by consumer). */
  generatedAt?: string;
  /** Logo node — defaults to plain text "Statura". */
  logo?: React.ReactNode;
  /** Optional footer content rendered alongside page numbers. */
  footerNote?: React.ReactNode;
  /** Print content. */
  children: React.ReactNode;
  className?: string;
}

export function PrintView({
  title,
  subtitle,
  tenantName,
  generatedAt,
  logo,
  footerNote,
  children,
  className,
}: PrintViewProps) {
  return (
    <article
      className={cn('print-view bg-white text-statura-ink', className)}
      data-print-view
      data-print-document
    >
      {/* Print-only CSS — scoped via the @media print bracket inside the
          consumer's globals.css would also work, but inlining here keeps
          this primitive self-contained. */}
      <style>{`
        @media print {
          @page { margin: 18mm 14mm 22mm 14mm; }
          html, body { background: #ffffff !important; }
          .print-view { max-width: none !important; }
          .print-view .print-section { break-inside: avoid; page-break-inside: avoid; }
          .print-view table { break-inside: auto; }
          .print-view tr, .print-view td, .print-view th { break-inside: avoid; page-break-inside: avoid; }
          .print-view thead { display: table-header-group; }
          .print-view tfoot { display: table-footer-group; }
          /* Hide anything the app marked screen-only */
          .screen-only { display: none !important; }
        }
      `}</style>

      <header
        className="print-view-header border-b-2 border-statura-green-accessible/40 pb-3 mb-4"
        data-print-header
      >
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-statura-green-accessible">
              {tenantName ?? 'Statura Care'}
            </div>
            <PageTitle className="mt-0.5 font-heading text-lg font-bold text-statura-ink">
              {title}
            </PageTitle>
            {subtitle && <p className="text-[11.5px] text-slate-600">{subtitle}</p>}
          </div>
          <div className="shrink-0 text-right">
            <div className="font-heading text-base font-bold text-statura-ink">
              {logo ?? 'Statura'}
            </div>
            {generatedAt && (
              <p className="mt-0.5 font-mono text-[10px] text-slate-500">Generated {generatedAt}</p>
            )}
          </div>
        </div>
      </header>

      <div className="print-view-body space-y-4 text-[12px] leading-relaxed">{children}</div>

      <footer
        className="print-view-footer mt-6 border-t border-slate-200 pt-2 text-[10px] text-slate-500"
        data-print-footer
      >
        <div className="flex items-center justify-between gap-4">
          <span>{footerNote ?? 'Confidential — for the named recipient only.'}</span>
          <span className="font-mono">{tenantName ?? 'Statura Care'}</span>
        </div>
      </footer>
    </article>
  );
}
